"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Section, Button } from "@/src/components";
import { cn } from "@/utils/utils";

interface AnimationDemoProps {
  name: string;
  token: string;
  value: string;
  description: string;
}

function DurationDemo({ name, token, value, description }: AnimationDemoProps) {
  const [animate, setAnimate] = React.useState(false);

  return (
    <div className="flex items-center gap-4 py-4 border-b last:border-b-0">
      <div className="w-24">
        <Text className="font-semibold">{name}</Text>
        <Text className="text-xs text-muted-foreground">{value}</Text>
      </div>
      <div className="flex-1">
        <div
          className={cn(
            "h-10 w-10 bg-primary rounded-lg",
            animate && "translate-x-48"
          )}
          style={{
            transitionProperty: "transform",
            transitionDuration: value,
            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setAnimate(!animate)}
      >
        {animate ? "Reset" : "Play"}
      </Button>
      <div className="w-48 text-sm text-muted-foreground">
        {description}
      </div>
    </div>
  );
}

function EasingDemo({ name, value, description }: { name: string; value: string; description: string }) {
  const [animate, setAnimate] = React.useState(false);

  return (
    <div className="flex items-center gap-4 py-4 border-b last:border-b-0">
      <div className="w-24">
        <Text className="font-semibold">{name}</Text>
      </div>
      <div className="flex-1">
        <div
          className={cn(
            "h-10 w-10 bg-primary rounded-lg",
            animate && "translate-x-48"
          )}
          style={{
            transitionProperty: "transform",
            transitionDuration: "500ms",
            transitionTimingFunction: value,
          }}
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setAnimate(!animate)}
      >
        {animate ? "Reset" : "Play"}
      </Button>
      <div className="w-48 text-sm text-muted-foreground">
        {description}
      </div>
    </div>
  );
}

const durations = [
  { name: "Fast", token: "--duration-fast", value: "100ms", description: "Micro-interactions: hover, focus" },
  { name: "Normal", token: "--duration-normal", value: "200ms", description: "Standard transitions: dropdowns" },
  { name: "Slow", token: "--duration-slow", value: "300ms", description: "Page transitions, modals" },
];

const easings = [
  { name: "Default", token: "--ease-default", value: "cubic-bezier(0.4, 0, 0.2, 1)", description: "General purpose" },
  { name: "In", token: "--ease-in", value: "cubic-bezier(0.4, 0, 1, 1)", description: "Elements entering" },
  { name: "Out", token: "--ease-out", value: "cubic-bezier(0, 0, 0.2, 1)", description: "Elements leaving" },
  { name: "Bounce", token: "--ease-bounce", value: "cubic-bezier(0.34, 1.56, 0.64, 1)", description: "Playful interactions" },
];

const componentAnimations = [
  { component: "Button", property: "transform, background", duration: "fast", easing: "default" },
  { component: "Input", property: "border-color, shadow", duration: "fast", easing: "default" },
  { component: "Modal/Sheet", property: "opacity, transform", duration: "slow", easing: "out" },
  { component: "Dropdown", property: "opacity, scale", duration: "normal", easing: "out" },
  { component: "Tooltip", property: "opacity", duration: "fast", easing: "default" },
  { component: "Tabs", property: "background", duration: "normal", easing: "default" },
  { component: "Switch", property: "background, transform", duration: "normal", easing: "default" },
  { component: "Accordion", property: "height, opacity", duration: "normal", easing: "default" },
];

export default function DocsTokensAnimation() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Animation Tokens
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Consistent motion creates a polished, professional feel. Transitions signal
            interactivity and provide feedback to users.
          </Text>
        </div>
      </Section>

      {/* Duration Scale */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Duration Scale</CardTitle>
            <CardDescription>Click Play to see each duration in action</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        {durations.map((duration) => (
          <DurationDemo key={duration.token} {...duration} />
        ))}
      </SectionCard>
      </Section>

      {/* Easing Functions */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Easing Functions</CardTitle>
            <CardDescription>Different timing functions for various contexts</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        {easings.map((easing) => (
          <EasingDemo key={easing.token} {...easing} />
        ))}
      </SectionCard>
      </Section>

      {/* Component Guidelines */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Component Animation Guidelines</CardTitle>
            <CardDescription>Recommended animation settings per component type</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Component</th>
              <th className="text-left py-2 font-semibold">Property</th>
              <th className="text-left py-2 font-semibold">Duration</th>
              <th className="text-left py-2 font-semibold">Easing</th>
            </tr>
          </thead>
          <tbody>
            {componentAnimations.map((anim) => (
              <tr key={anim.component} className="border-b last:border-b-0">
                <td className="py-2 font-medium">{anim.component}</td>
                <td className="py-2 text-muted-foreground">{anim.property}</td>
                <td className="py-2">
                  <code className="bg-muted px-2 py-0.5 rounded text-xs">{anim.duration}</code>
                </td>
                <td className="py-2">
                  <code className="bg-muted px-2 py-0.5 rounded text-xs">{anim.easing}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* Tailwind Utilities */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Tailwind Utilities</CardTitle>
            <CardDescription>Custom utility classes for animation tokens</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Text className="font-semibold">Duration</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block">.transition-fast</code>
              <code className="block">.transition-normal</code>
              <code className="block">.transition-slow</code>
            </div>
          </div>
          
          <div className="space-y-3">
            <Text className="font-semibold">Easing</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block">.ease-default</code>
              <code className="block">.ease-in</code>
              <code className="block">.ease-out</code>
              <code className="block">.ease-bounce</code>
            </div>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Reduced Motion */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Reduced Motion Support</CardTitle>
            <CardDescription>Respecting user preferences for reduced motion</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <Text className="text-sm text-muted-foreground">
          All animations automatically respect the user's motion preferences via the
          <code className="mx-1 bg-muted px-1 rounded">prefers-reduced-motion</code>
          media query.
        </Text>
        
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
          <code className="block">{"@media (prefers-reduced-motion: reduce) {"}</code>
          <code className="block pl-4">{"*, *::before, *::after {"}</code>
          <code className="block pl-8">{"animation-duration: 0.01ms !important;"}</code>
          <code className="block pl-8">{"transition-duration: 0.01ms !important;"}</code>
          <code className="block pl-4">{"}"}</code>
          <code className="block">{"}"}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
        header={<CardTitle>Usage in Style Drivers</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
          <code className="block">{"export const buttonStyleDriver = {"}</code>
          <code className="block pl-4">{"root: cn("}</code>
          <code className="block pl-8">{'"inline-flex items-center",'}</code>
          <code className="block pl-8">{'"transition-all transition-fast ease-default",'}</code>
          <code className="block pl-8">{'"hover:scale-[1.02] active:scale-[0.98]"'}</code>
          <code className="block pl-4">{"),"}</code>
          <code className="block">{"}"}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard
          header={<CardTitle className="text-success">✓ Do</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Use fast for micro-interactions</li>
            <li>• Use slow for overlays and modals</li>
            <li>• Always include transition properties</li>
            <li>• Test with reduced motion enabled</li>
            <li>• Use CSS for animations, not JS</li>
          </ul>
        </SectionCard>

        <SectionCard
          header={<CardTitle className="text-destructive">✗ Don't</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Don't animate without purpose</li>
            <li>• Don't use long durations ({">"}500ms)</li>
            <li>• Don't animate layout-triggering properties</li>
            <li>• Don't forget reduced motion support</li>
            <li>• Don't use arbitrary duration values</li>
          </ul>
        </SectionCard>
        </div>
      </Section>
    </>
  );
}
